/**
 * AG-UI Response Widgets
 *
 * React components for rendering multi-agent Statcast responses.
 * These widgets display data, insights, and strategy recommendations
 * in a rich, interactive format.
 *
 * Based on AG-UI Protocol: https://docs.ag-ui.com/introduction
 */

import React, { useState } from 'react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import {
  ChevronDown,
  ChevronUp,
  Database,
  Lightbulb,
  Target,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Clock,
  Brain,
  Zap,
} from 'lucide-react';
import { marked } from 'marked';

// ============================================================================
// WIDGET TYPE CONSTANTS
// ============================================================================

const WidgetType = {
  ANSWER: 'answer',
  DATA_TABLE: 'data_table',
  CHART: 'chart',
  STRATEGY: 'strategy',
  THINKING: 'thinking',
  ERROR: 'error',
};

// ============================================================================
// THEME COLORS
// ============================================================================

const CHART_COLORS = {
  primary: '#3b82f6',
  secondary: '#10b981',
  accent: '#f59e0b',
  error: '#ef4444',
  muted: '#6b7280',
  gradient: ['#3b82f6', '#8b5cf6', '#06b6d4', '#10b981', '#f59e0b'],
};

// ============================================================================
// ANSWER WIDGET
// ============================================================================

/**
 * AnswerWidget - Displays natural language response with markdown
 */
export const AnswerWidget = ({ data, metadata = {} }) => {
  const { answer, sources = [], confidence } = data;

  return (
    <div className="answer-widget bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
      {/* Header */}
      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-100 dark:border-gray-700">
        <Lightbulb className="w-5 h-5 text-yellow-500" />
        <span className="font-semibold text-gray-800 dark:text-gray-200">Analysis</span>
        {confidence && (
          <span className="ml-auto text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded-full">
            {Math.round(confidence * 100)}% confidence
          </span>
        )}
      </div>

      {/* Content */}
      <div
        className="prose prose-sm dark:prose-invert max-w-none"
        dangerouslySetInnerHTML={{ __html: marked(answer || '') }}
      />

      {/* Sources */}
      {sources.length > 0 && (
        <div className="mt-3 pt-2 border-t border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
            <Database className="w-3 h-3" />
            <span>Sources: {sources.join(', ')}</span>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// DATA TABLE WIDGET
// ============================================================================

/**
 * DataTableWidget - Displays structured data in a table
 */
export const DataTableWidget = ({ data, metadata = {} }) => {
  const { rows = [], columns = [], title, summary, rowCount } = data;
  const [isExpanded, setIsExpanded] = useState(false);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  // Limit displayed rows
  const displayedRows = isExpanded ? rows : rows.slice(0, 5);

  // Sort function
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Sort rows
  const sortedRows = [...displayedRows].sort((a, b) => {
    if (!sortColumn) return 0;
    const aVal = a[sortColumn];
    const bVal = b[sortColumn];
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    }
    return sortDirection === 'asc'
      ? String(aVal).localeCompare(String(bVal))
      : String(bVal).localeCompare(String(aVal));
  });

  // Format cell value
  const formatValue = (value) => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'number') {
      return value % 1 === 0 ? value : value.toFixed(2);
    }
    return String(value);
  };

  // Format column header
  const formatHeader = (col) => {
    return col
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  return (
    <div className="data-table-widget bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <Database className="w-5 h-5 text-blue-500" />
          <span className="font-semibold text-gray-800 dark:text-gray-200">
            {title || 'Query Results'}
          </span>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            ({rowCount || rows.length} rows)
          </span>
        </div>
      </div>

      {/* Summary */}
      {summary && (
        <div className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/50">
          {summary}
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
            <tr>
              {columns.slice(0, 8).map((col) => (
                <th
                  key={col}
                  onClick={() => handleSort(col)}
                  className="px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                  <div className="flex items-center gap-1">
                    {formatHeader(col)}
                    {sortColumn === col && (
                      <span className="text-blue-500">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row, idx) => (
              <tr
                key={idx}
                className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50"
              >
                {columns.slice(0, 8).map((col) => (
                  <td
                    key={col}
                    className="px-3 py-2 text-gray-700 dark:text-gray-300"
                  >
                    {formatValue(row[col])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Expand/Collapse */}
      {rows.length > 5 && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full px-4 py-2 text-sm text-blue-600 dark:text-blue-400 hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center justify-center gap-1 border-t border-gray-200 dark:border-gray-700"
        >
          {isExpanded ? (
            <>
              <ChevronUp className="w-4 h-4" />
              Show less
            </>
          ) : (
            <>
              <ChevronDown className="w-4 h-4" />
              Show all {rows.length} rows
            </>
          )}
        </button>
      )}
    </div>
  );
};

// ============================================================================
// CHART WIDGET
// ============================================================================

/**
 * ChartWidget - Displays visualizations using Recharts
 */
export const ChartWidget = ({ data, metadata = {} }) => {
  const { chartType, data: chartData, config = {} } = data;
  const { title, xAxis, yAxis, xLabel, yLabel, colors = CHART_COLORS.gradient } = config;

  if (!chartData || chartData.length === 0) {
    return (
      <div className="chart-widget bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <p className="text-gray-500 dark:text-gray-400 text-center">No data available for chart</p>
      </div>
    );
  }

  const renderChart = () => {
    const commonProps = {
      data: chartData,
      margin: { top: 20, right: 30, left: 20, bottom: 20 },
    };

    switch (chartType) {
      case 'line':
        return (
          <LineChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey={xAxis}
              tick={{ fontSize: 12 }}
              label={{ value: xLabel, position: 'bottom', offset: -5 }}
            />
            <YAxis
              tick={{ fontSize: 12 }}
              label={{ value: yLabel, angle: -90, position: 'insideLeft' }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#1f2937',
                border: 'none',
                borderRadius: '8px',
                color: '#fff',
              }}
            />
            <Legend />
            <Line
              type="monotone"
              dataKey={yAxis}
              stroke={CHART_COLORS.primary}
              strokeWidth={2}
              dot={{ fill: CHART_COLORS.primary, strokeWidth: 2 }}
            />
          </LineChart>
        );

      case 'scatter':
        return (
          <ScatterChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey={xAxis}
              type="number"
              tick={{ fontSize: 12 }}
              label={{ value: xLabel, position: 'bottom', offset: -5 }}
            />
            <YAxis
              dataKey={yAxis}
              type="number"
              tick={{ fontSize: 12 }}
              label={{ value: yLabel, angle: -90, position: 'insideLeft' }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#1f2937',
                border: 'none',
                borderRadius: '8px',
                color: '#fff',
              }}
            />
            <Scatter name={title} fill={CHART_COLORS.primary} />
          </ScatterChart>
        );

      case 'bar':
      default:
        return (
          <BarChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey={xAxis}
              tick={{ fontSize: 11, angle: -45, textAnchor: 'end' }}
              height={60}
            />
            <YAxis
              tick={{ fontSize: 12 }}
              label={{ value: yLabel, angle: -90, position: 'insideLeft' }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#1f2937',
                border: 'none',
                borderRadius: '8px',
                color: '#fff',
              }}
            />
            <Legend />
            <Bar dataKey={yAxis} radius={[4, 4, 0, 0]}>
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
              ))}
            </Bar>
          </BarChart>
        );
    }
  };

  return (
    <div className="chart-widget bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <TrendingUp className="w-5 h-5 text-green-500" />
        <span className="font-semibold text-gray-800 dark:text-gray-200">
          {title || 'Visualization'}
        </span>
      </div>

      {/* Chart */}
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          {renderChart()}
        </ResponsiveContainer>
      </div>
    </div>
  );
};

// ============================================================================
// STRATEGY WIDGET
// ============================================================================

/**
 * StrategyWidget - Displays actionable recommendations
 */
export const StrategyWidget = ({ data, metadata = {} }) => {
  const { recommendations = [], summary, confidence } = data;

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 border-red-200 dark:border-red-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 border-yellow-200 dark:border-yellow-800';
      case 'low':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 border-green-200 dark:border-green-800';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-600';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'high':
        return <Zap className="w-4 h-4" />;
      case 'medium':
        return <Target className="w-4 h-4" />;
      case 'low':
        return <CheckCircle className="w-4 h-4" />;
      default:
        return <Lightbulb className="w-4 h-4" />;
    }
  };

  return (
    <div className="strategy-widget bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-900 rounded-lg border border-blue-200 dark:border-blue-800 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-blue-100 dark:bg-blue-900/50 border-b border-blue-200 dark:border-blue-800">
        <div className="flex items-center gap-2">
          <Target className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          <span className="font-bold text-blue-900 dark:text-blue-100">Strategy Recommendations</span>
        </div>
        {confidence && (
          <span className="text-xs bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-200 px-2 py-1 rounded-full">
            {Math.round(confidence * 100)}% confidence
          </span>
        )}
      </div>

      {/* Summary */}
      {summary && (
        <div className="px-4 py-3 text-sm font-medium text-blue-800 dark:text-blue-200 bg-blue-50 dark:bg-blue-900/30 border-b border-blue-200 dark:border-blue-800">
          <Brain className="w-4 h-4 inline mr-2" />
          {summary}
        </div>
      )}

      {/* Recommendations */}
      <div className="p-4 space-y-3">
        {recommendations.map((rec, idx) => (
          <div
            key={idx}
            className={`flex items-start gap-3 p-3 rounded-lg border ${getPriorityColor(rec.priority)}`}
          >
            <div className="flex-shrink-0 mt-0.5">
              {getPriorityIcon(rec.priority)}
            </div>
            <div className="flex-grow">
              <div className="font-semibold text-sm mb-1">{rec.title}</div>
              <div className="text-sm opacity-90">{rec.description}</div>
              {rec.rationale && (
                <div className="text-xs opacity-70 mt-1 italic">
                  {rec.rationale}
                </div>
              )}
            </div>
            <div className="flex-shrink-0">
              <span className="text-xs uppercase font-bold opacity-70">
                {rec.priority}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// THINKING WIDGET
// ============================================================================

/**
 * ThinkingWidget - Shows agent reasoning/processing steps
 */
export const ThinkingWidget = ({ data, metadata = {} }) => {
  const { agentId, steps = [] } = data;
  const [isExpanded, setIsExpanded] = useState(false);

  const getAgentName = (id) => {
    const names = {
      'statcast-nl2sql': 'NL2SQL Agent',
      'statcast-insight': 'Insight Agent',
      'statcast-strategy': 'Strategy Agent',
      'statcast-orchestrator': 'Orchestrator',
    };
    return names[id] || id;
  };

  const getStepIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'running':
        return <Clock className="w-4 h-4 text-blue-500 animate-spin" />;
      case 'error':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  return (
    <div className="thinking-widget bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-800"
      >
        <div className="flex items-center gap-2">
          <Brain className="w-4 h-4 text-purple-500" />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {getAgentName(agentId)} Thinking
          </span>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4 text-gray-400" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-400" />
        )}
      </button>

      {/* Steps */}
      {isExpanded && steps.length > 0 && (
        <div className="px-4 pb-3 space-y-2">
          {steps.map((step, idx) => (
            <div key={idx} className="flex items-center gap-2 text-sm">
              {getStepIcon(step.status)}
              <span className="text-gray-600 dark:text-gray-400">{step.step}</span>
              {step.duration && (
                <span className="ml-auto text-xs text-gray-400">
                  {step.duration}ms
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// ERROR WIDGET
// ============================================================================

/**
 * ErrorWidget - Displays error messages
 */
export const ErrorWidget = ({ data, metadata = {} }) => {
  const { message, code, details } = data;

  return (
    <div className="error-widget bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800 p-4">
      <div className="flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
        <div>
          <div className="font-semibold text-red-800 dark:text-red-200">{message}</div>
          {code && (
            <div className="text-xs text-red-600 dark:text-red-400 mt-1">
              Error code: {code}
            </div>
          )}
          {details && (
            <div className="text-sm text-red-700 dark:text-red-300 mt-2">{details}</div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// WIDGET RENDERER
// ============================================================================

/**
 * WidgetRenderer - Renders the appropriate widget based on type
 */
export const WidgetRenderer = ({ widget }) => {
  if (!widget || !widget.widgetType) {
    return null;
  }

  const { widgetType, ...data } = widget;

  switch (widgetType) {
    case WidgetType.ANSWER:
      return <AnswerWidget data={data} />;
    case WidgetType.DATA_TABLE:
      return <DataTableWidget data={data} />;
    case WidgetType.CHART:
      return <ChartWidget data={data} />;
    case WidgetType.STRATEGY:
      return <StrategyWidget data={data} />;
    case WidgetType.THINKING:
      return <ThinkingWidget data={data} />;
    case WidgetType.ERROR:
      return <ErrorWidget data={data} />;
    default:
      console.warn(`Unknown widget type: ${widgetType}`);
      return null;
  }
};

// ============================================================================
// EXPORTS
// ============================================================================

// Note: Components are exported inline above (export const ...)
// Only export WidgetType and default here
export { WidgetType };
export default WidgetRenderer;
